'use client';

import { MicIcon } from './icons';
import type { VoiceStrings } from '@/lib/voiceInput';

interface VoiceMicButtonProps {
  listening: boolean;
  /** A short error to show next to the mic, or null. */
  message: string | null;
  onClick: () => void;
  strings: VoiceStrings;
  disabled?: boolean;
  /**
   * Where the error bubble opens. 'below' for the search bar at the top of
   * the modal; 'above' for the chat input at the bottom of its panel, where a
   * bubble below would be cut off.
   */
  messagePlacement?: 'below' | 'above';
}

/**
 * The mic control shared by the search bar and the Αριάδνη chat input.
 * Presentational only — `useVoiceInput` owns the recognition.
 *
 * The button itself stays in the flex row (absolutely-positioned CONTROLS in
 * the search bar row are a known trap — see SearchBar). Only the transient
 * error bubble is positioned, so it never shifts the layout.
 */
export default function VoiceMicButton({
  listening,
  message,
  onClick,
  strings,
  disabled,
  messagePlacement = 'below',
}: VoiceMicButtonProps) {
  return (
    <div className="relative flex-shrink-0">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        // A constant name plus a pressed state — screen readers announce
        // "Φωνητική αναζήτηση, pressed"; a name that also flipped would read
        // as a different control each time.
        aria-label={strings.start}
        aria-pressed={listening}
        title={listening ? strings.stop : strings.start}
        className={`relative flex items-center justify-center w-9 h-9 rounded-full cursor-pointer transition-colors disabled:opacity-30 disabled:cursor-default ${
          listening ? 'text-brand' : 'text-[#99A1AF] hover:text-text-primary'
        }`}
      >
        {listening && (
          // Static ring when the shopper asked for reduced motion.
          <span aria-hidden="true" className="absolute inset-0.5 rounded-full bg-brand/20 motion-safe:animate-micPulse" />
        )}
        <MicIcon className="relative w-5 h-5" />
      </button>
      {/* Always in the DOM so screen readers announce the text when it
          changes — a live region that appears already filled is often
          skipped (VoiceOver especially). */}
      <span role="status" aria-live="polite" className="sr-only">
        {message ?? ''}
      </span>
      {message && (
        <div
          aria-hidden="true"
          // z-50: above every layer in the modal — the results header is a
          // sticky z-20 that comes LATER in the DOM, so a z-20 bubble was cut
          // in half by it (measured), and the phone drawers are z-30.
          // Width capped by the viewport too: the phone chat panel is ~280px.
          className={`absolute right-0 z-50 w-max max-w-[min(240px,60vw)] px-3 py-2 bg-black text-white text-[12px] leading-snug font-gotham shadow-lg ${
            messagePlacement === 'above' ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
        >
          {message}
        </div>
      )}
    </div>
  );
}
